import React, { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import Badge from "../../components/ui-components/Badge";
import Modal from "../../components/ui-components/Modal";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { toast } from "../../components/ui-components/Toast";
import { listInboundTokens, createInboundToken, revokeInboundToken } from "./invoiceInboundTokensApi";

// fmtDate renders an ISO timestamp as a short local date-time. Returns "—" for empty input.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// CopyField renders a read-only value with a Copy button. Used for the secret and the inbound address.
const CopyField = ({ value, mono }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — clipboard access blocked");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <code className={`flex-1 bg-surface-2 border border-card-bd rounded-ctrl px-3 py-2 text-sm break-all ${mono ? "font-mono" : ""}`}>
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="px-3 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors shrink-0"
      >
        Copy
      </button>
    </div>
  );
};

const StatusBadge = ({ status }) =>
  status === "revoked"
    ? <Badge label="Revoked" status="cancelled" size="sm" />
    : <Badge label="Active" status="success" size="sm" />;

const InvoiceInboundTokensPage = () => {
  const queryClient = useQueryClient();
  const { data: tokens = [], isLoading, error } = useQuery(
    ["invoice-inbound-tokens"],
    listInboundTokens
  );

  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState("");
  const [revealed, setRevealed] = useState(null); // the one-time secret object; never persisted
  const [revokeTarget, setRevokeTarget] = useState(null);

  const notEnabled = error?.status === 503;

  const createMutation = useMutation(() => createInboundToken(label.trim()), {
    onSuccess: (created) => {
      setShowCreate(false);
      setLabel("");
      setRevealed(created);          // secret lives only here
      queryClient.invalidateQueries(["invoice-inbound-tokens"]);
    },
    onError: () => toast.error("Couldn't generate the token. Please try again."),
  });

  const revokeMutation = useMutation((id) => revokeInboundToken(id), {
    onSuccess: () => {
      toast.success("Token revoked");
      queryClient.invalidateQueries(["invoice-inbound-tokens"]);
      setRevokeTarget(null);
    },
    onError: () => toast.error("Couldn't revoke the token. Please try again."),
  });

  const closeReveal = () => setRevealed(null);

  const openCreate = () => { setLabel(""); setShowCreate(true); };

  const columns = useMemo(
    () => [
      {
        accessor: "display",
        Header: () => <span>Token</span>,
        Cell: ({ value }) => <span className="font-mono text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "label",
        Header: () => <span>Label</span>,
        Cell: ({ value }) =>
          value ? <span className="text-text-2 truncate">{value}</span> : <span className="text-text-3">—</span>,
        flex: 1,
        minWidth: 140,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusBadge status={value} />,
        width: 110,
        minWidth: 100,
      },
      {
        accessor: "created_at",
        Header: () => <span>Created</span>,
        Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>,
        width: 170,
        minWidth: 140,
      },
      {
        accessor: "last_used_at",
        Header: () => <span>Last used</span>,
        Cell: ({ value }) =>
          value ? <span className="text-text-2">{fmtDate(value)}</span> : <span className="text-text-3">Never used</span>,
        width: 170,
        minWidth: 140,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) =>
          row.original.status === "revoked" ? null : (
            <button
              type="button"
              onClick={() => setRevokeTarget(row.original)}
              className="px-3 py-1 text-sm rounded-ctrl text-danger hover:bg-danger-soft transition-colors"
            >
              Revoke
            </button>
          ),
        width: 110,
        minWidth: 100,
      },
    ],
    []
  );

  const noun = tokens.length === 1 ? "token" : "tokens";

  return (
    <Fragment>
      <AdministrationHeader title="Invoice inbound tokens" showPeriodSelector={false} />
      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
        <p className="text-sm text-text-2 mb-4 max-w-2xl">
          Generate a token to get a dedicated address you can forward vendor invoices to. Every
          message forwarded to that address is picked up automatically for ingestion.
        </p>

        {notEnabled ? (
          <div className="bg-card border border-card-bd rounded-card px-6 py-12 text-center">
            <div className="text-sm font-semibold text-text mb-1">Invoice ingestion isn't enabled yet</div>
            <p className="text-sm text-text-2">
              Token minting becomes available once invoice ingestion is configured for your account.
            </p>
          </div>
        ) : (
          <TableCard
            title="Inbound tokens"
            subtitle={isLoading ? "Loading…" : `Showing ${tokens.length} ${noun}`}
            primaryAction={{ label: "Generate token", icon: icon({ name: "plus" }), onClick: openCreate }}
            isLoading={isLoading}
            error={error}
            errorMessage="Error loading inbound tokens"
            onRetry={() => queryClient.invalidateQueries(["invoice-inbound-tokens"])}
          >
            {tokens.length === 0 ? (
              <EmptyState
                icon={icon({ name: "key", style: "light" })}
                title="No inbound tokens yet"
                message="Generate a token to get an address you can forward vendor invoices to."
                action={{ label: "Generate token", onClick: openCreate, icon: icon({ name: "plus", style: "regular" }) }}
              />
            ) : (
              <FlexTable columns={columns} data={tokens} getRowId={(t) => t.id} />
            )}
          </TableCard>
        )}
      </div>

      {/* Create modal */}
      <Modal
        show={showCreate}
        close={() => setShowCreate(false)}
        title="Generate inbound token"
        description="Optionally label this token so you can tell your inboxes apart. The secret is shown once."
        icon={icon({ name: "key", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={createMutation.isLoading ? "Generating…" : "Generate"}
        confirmAction={() => { if (!createMutation.isLoading) createMutation.mutate(); }}
      >
        <div className="mb-4">
          <label htmlFor="inbound-token-label" className="block mb-1 text-sm font-medium text-text">
            Label (optional)
          </label>
          <input
            id="inbound-token-label"
            type="text"
            value={label}
            maxLength={100}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="AP inbox"
            className="border border-border rounded-ctrl px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </Modal>

      {/* One-time reveal modal — explicit dismiss only */}
      <Modal
        show={!!revealed}
        close={closeReveal}
        title="Copy your inbound token now"
        description="This is the only time the secret is shown. Forward vendor invoices to the address below."
        icon={icon({ name: "key", style: "solid" })}
        cancelButton=""
        confirmButton="Done"
        confirmAction={closeReveal}
        size="lg"
      >
        {revealed && (
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-sm font-medium text-text mb-1">Token (secret)</div>
              <CopyField value={revealed.token} mono />
              <p className="text-xs font-semibold text-danger mt-1">
                Copy this now — you will not be able to see it again.
              </p>
            </div>
            <div>
              <div className="text-sm font-medium text-text mb-1">Inbound address</div>
              <CopyField value={revealed.inbound_address} />
              <p className="text-xs text-text-2 mt-1">
                Forward vendor invoices to this address — they'll be picked up automatically.
              </p>
            </div>
            {revealed.note && (
              <p className="text-xs text-text-2">{revealed.note}</p>
            )}
          </div>
        )}
      </Modal>
      {/* Revoke confirm */}
      <Modal
        show={!!revokeTarget}
        close={() => setRevokeTarget(null)}
        title="Revoke this token?"
        description={
          <span>
            Revoking takes effect immediately and breaks forwarding for
            {revokeTarget?.label ? ` "${revokeTarget.label}"` : " this token"}.
            To rotate without downtime: create a new token, update where invoices are forwarded to
            the new address, then revoke the old one.
          </span>
        }
        alertIcon
        icon={icon({ name: "ban", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={revokeMutation.isLoading ? "Revoking…" : "Revoke token"}
        confirmAction={() => { if (!revokeMutation.isLoading && revokeTarget) revokeMutation.mutate(revokeTarget.id); }}
      />
    </Fragment>
  );
};

export default InvoiceInboundTokensPage;
